import React from "react"
import {Box} from "@mui/material"
import ChartLayer from "./ChartLayer"
import EventLayer from "./EventLayer"
import TimelineAxis from "./TimelineAxis"
import LegendRow from "./LegendRow"

export const eventsAndBarchartPrototype = [
    {key: "startDate", type: "INTEGER", label: "Start-Jahr", size: 3},
    {key: "endDate", type: "INTEGER", label: "End-Jahr", size: 3},
    {key: "minHeight", type: "INTEGER", label: "Chart-Höhe (px)", size: 3},
    {key: "eventTopSpace", type: "INTEGER", label: "Event-Raum oben (px)", size: 3},
    {key: "axisLeftOffset", type: "INTEGER", label: "Achse links Offset (px)", size: 3},
    {key: "axisRightOffset", type: "INTEGER", label: "Achse rechts Offset (px)", size: 3},
    {
        key: "legend",
        type: "SELECT",
        label: "Legende",
        size: 3,
        options: [
            {value: "NONE", label: "Keine"},
            {value: "BOTTOM", label: "Unten"},
            {value: "RIGHT", label: "Rechts"},
        ],
    },
]

const parseIntOr = (value, fallback) => {
    const parsed = parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : fallback
}

const EventsAndBarchartWidget = ({widget, value, onValueChange}) => {
    const effectiveValue = value 
    const startYear = parseIntOr(widget?.startDate, 1990)
    const endYear = parseIntOr(widget?.endDate, 2090)
    const chartHeight = parseIntOr(widget?.minHeight, 272)
    const eventTopSpace = parseIntOr(widget?.eventTopSpace, 63)
    const axisLeftOffset = parseIntOr(widget?.axisLeftOffset, 70)
    const axisRightOffset = parseIntOr(widget?.axisRightOffset, 20)

    const [containerWidth, setContainerWidth] = React.useState(0)
    const containerRef = React.useRef(null)

    React.useEffect(() => {
        if (!containerRef.current) return
        const observer = new ResizeObserver((entries) => {
            setContainerWidth(entries[0].contentRect.width)
        })
        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    const chartInnerWidth = Math.max(0, containerWidth - axisLeftOffset - axisRightOffset)
    const stackHeight = chartHeight + eventTopSpace

    const handleEventsChange = (newEvents) => {
        onValueChange?.({...effectiveValue, events: newEvents})
    }

    return (
        <Box
            ref={containerRef}
            sx={{position: "relative", width: "100%"}}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: `${stackHeight}px`,
                    minHeight: `${stackHeight}px`,
                    pt: `${eventTopSpace}px`,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${chartHeight}px`,
                    }}
                >
                    <ChartLayer
                        chart={effectiveValue?.chart}
                        legend={widget?.legend === "BOTTOM" ? "NONE" : widget?.legend}
                        height={chartHeight}
                    />
                </Box>
                {containerWidth > 0 && (
                    <EventLayer
                        events={effectiveValue?.events ?? []}
                        startYear={startYear}
                        endYear={endYear}
                        onEventsChange={handleEventsChange}
                        leftOffsetPx={axisLeftOffset}
                        widthPx={chartInnerWidth}
                        height={stackHeight}
                    />
                )}
            </Box>
            {containerWidth > 0 && (
                <TimelineAxis
                    startYear={startYear}
                    endYear={endYear}
                    rentebeginn={effectiveValue?.rentebeginn}
                    leftOffsetPx={axisLeftOffset}
                    widthPx={chartInnerWidth}
                />
            )}
            {widget?.legend === "BOTTOM" && (
                <Box sx={{pl: `${axisLeftOffset}px`, pr: `${axisRightOffset}px`}}>
                    <LegendRow datasets={effectiveValue?.chart?.datasets ?? []} />
                </Box>
            )}
        </Box>
    )
}

export default EventsAndBarchartWidget
